import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { QueryKeys } from "@/queries/utils";
import {
  AdminJobCandidateDetails,
  AdminJobDetails,
} from "@/queries/admin/job-details";
import { NewBillingPlan } from "@/types";

export const useCreateBillingPlan = (args: {
  jobOpeningId: string;
  candidateStatusId: string;
  onSuccess?: (data: AdminJobCandidateDetails) => void;
}) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (plan: NewBillingPlan) => {
      const token = await getAccessTokenSilently();

      return request<AdminJobCandidateDetails>({
        method: "PUT",
        path: `/api/admin/jobOpening/${args.jobOpeningId}/candidate/${args.candidateStatusId}/billing-plan`,
        body: JSON.stringify(plan),
        token,
      });
    },
    onSuccess(data) {
      args.onSuccess?.(data);

      toast.success("Billing plan created!");

      qc.setQueryData<AdminJobDetails>(
        QueryKeys.adminJobDetails(args.jobOpeningId),
        (old) => {
          if (!old) {
            return old;
          }

          const candidates = [...old.candidates];
          const candidateIdx = candidates.findIndex(
            (candidate) => candidate.id === data.id,
          );

          if (candidateIdx === -1) {
            return old;
          }

          candidates[candidateIdx] = data;

          return {
            ...old,
            candidates,
          };
        },
      );
    },
  });

  return {
    createBillingPlan: mutate,
    isCreatingBillingPlan: isPending,
    createBillingPlanError: error,
  };
};
